import { Input } from '@mui/material';
import { WithStyles, withStyles } from '@mui/styles';
import { AgGridFilterType, SelectOption } from '@tassat/common-definitions';
import { StyledSelect } from '@tassat/formik-input';
import React from 'react';
import { SingleValue } from 'react-select';

interface FilterInputProps
    extends WithStyles<typeof muiStyles>,
    React.HTMLAttributes<HTMLDivElement> {
    type: 'text' | 'textEquals' | 'number' | 'select' | 'clientCounterparty' | 'assetAmount';
    filterValue: string;
    setFilterValue: React.Dispatch<React.SetStateAction<string>>;
    operation?: string;
    setOperation?: React.Dispatch<React.SetStateAction<string | undefined>>;
    filterAsset?: string;
    setFilterAsset?: React.Dispatch<React.SetStateAction<string | undefined>>;
    filterDirection?: string;
    setFilterDirection?: React.Dispatch<React.SetStateAction<string | undefined>>;
    selectOptions?: SelectOption[];
}

const muiStyles = () => ({
    root: { padding: '1rem' },
    selectInput: { padding: '1rem 0' },
    numberInput: { '&:focus': { boxSizing: 'content-box' as const }},
    button: { marginTop: '.5rem' }
});

export function FilterInput (props: FilterInputProps) {
    const {
        classes,
        id,
        type,
        filterValue,
        setFilterValue,
        operation,
        setOperation,
        filterDirection,
        setFilterDirection,
        selectOptions,
        filterAsset,
        setFilterAsset
    } = props;

    const operationOptions = () => {
        switch (type) {
            case 'text':
            case 'clientCounterparty':
                return [
                    {
                        label: 'Select an option...',
                        value: ''
                    },
                    {
                        label: 'Equals',
                        value: AgGridFilterType.Equal
                    },
                    {
                        label: 'Contains',
                        value: AgGridFilterType.Contains
                    }
                ];
            case 'textEquals':
                return [
                    {
                        label: 'Select an option...',
                        value: ''
                    },
                    {
                        label: 'Equals',
                        value: AgGridFilterType.Equal
                    }
                ];
            case 'number':
            case 'assetAmount':
                return [
                    {
                        label: 'Select an option...',
                        value: ''
                    },
                    {
                        label: 'Equals',
                        value: AgGridFilterType.Equal
                    },
                    {
                        label: 'Greater than',
                        value: AgGridFilterType.GreaterThan
                    },
                    {
                        label: 'Less than',
                        value: AgGridFilterType.LessThan
                    }
                ];
            default:
                return [];
        }
    };

    const renderInputType = () => {
        const operations = operationOptions();
        switch (type) {
            case 'text':
            case 'textEquals':
                return (
                    <>
                        <StyledSelect
                            className={classes.selectInput}
                            id={`${id}_operationSelect`}
                            menuPosition='fixed'
                            onChange={(selectedOption: SingleValue<SelectOption>) => {
                                setOperation(selectedOption?.value.toString());
                            }}
                            options={operations}
                            value={operations.find(opt => operation !== AgGridFilterType.ClearFilter && opt.value === operation)}
                        />
                        <Input
                            classes={{ input: classes.numberInput }}
                            id={`${id}_filterInput`}
                            inputProps={{ maxLength: 100 }}
                            onChange={(e) => {
                                const value = e.target.value;
                                setFilterValue(value);
                            }}
                            type='text'
                            value={filterValue}
                        />
                    </>
                );
            case 'number':
                return (
                    <>
                        <StyledSelect
                            className={classes.selectInput}
                            id={`${id}_operationSelect`}
                            menuPosition='fixed'
                            onChange={(selectedOption: SingleValue<SelectOption>) => {
                                setOperation(selectedOption?.value.toString());
                            }}
                            options={operations}
                            value={operations.find(opt => operation !== AgGridFilterType.ClearFilter && opt.value === operation)}
                        />
                        <Input
                            classes={{ input: classes.numberInput }}
                            id={`${id}_filterInput`}
                            onChange={(e) => {
                                const hasDecimal = e.target.value.includes('.');
                                const decimalValue = hasDecimal ? e.target.value.split('.')[1] : '';
                                const parsed: number = parseFloat(e.target.value);
                                const value = isNaN(parsed) ? '' : parsed.toFixed(decimalValue.length > 2 ? 2 : decimalValue.length);
                                setFilterValue(value);
                            }}
                            type='number'
                            value={filterValue}
                        />
                    </>
                );
            case 'select':
                return (
                    <>
                        <StyledSelect
                            className={classes.selectInput}
                            id={`${id}_filterInput`}
                            menuPosition='fixed'
                            onChange={(selectedOption: SingleValue<SelectOption>) => {
                                setFilterValue(selectedOption?.value.toString());
                            }}
                            options={selectOptions}
                            value={selectOptions?.find(opt => opt.value === filterValue)}
                        />
                    </>
                );
            case 'clientCounterparty': {
                const directionOptions = [
                    {
                        label: 'Select Direction...',
                        value: ''
                    },
                    {
                        label: 'Sender',
                        value: 'sender'
                    },
                    {
                        label: 'Recipient',
                        value: 'recipient'
                    }
                ];

                return (
                    <>
                        <StyledSelect
                            className={classes.selectInput}
                            id={`${id}_directionSelect`}
                            menuPosition='fixed'
                            onChange={(selectedOption: SingleValue<SelectOption>) => {
                                setFilterDirection(selectedOption?.value.toString());
                            }}
                            options={directionOptions}
                            value={directionOptions.find(dir => dir.value === filterDirection)}
                        />
                        <StyledSelect
                            className={classes.selectInput}
                            id={`${id}_operationSelect`}
                            menuPosition='fixed'
                            onChange={(selectedOption: SingleValue<SelectOption>) => {
                                setOperation(selectedOption?.value.toString());
                            }}
                            options={operations}
                            value={operations.find(opt => operation !== AgGridFilterType.ClearFilter && opt.value === operation)}
                        />
                        <Input
                            classes={{ input: classes.numberInput }}
                            id={`${id}_filterInput`}
                            inputProps={{ maxLength: 100 }}
                            onChange={(e) => {
                                const value = e.target.value;
                                setFilterValue(value);
                            }}
                            type='text'
                            value={filterValue}
                        />
                    </>
                );
            }
            case 'assetAmount': {
                // TODO Short term fix to avoid modifying existing logic
                // const assetOptions = [
                //     {
                //         label: 'Select an option...',
                //         value: ''
                //     },
                //     {
                //         label: AssetDropdownValues.USD,
                //         value: AssetDropdownValues.USD
                //     },
                //     {
                //         label: AssetDropdownValues.TFUND,
                //         value: AssetDropdownValues.TFUND
                //     }
                // ];

                return (
                    <>
                        {/* <StyledSelect<SelectOption, false>
                            className={classes.selectInput}
                            id={`${id}_directionSelect`}
                            menuPosition='fixed'
                            onChange={(selectedOption: SingleValue<SelectOption>) => {
                                setFilterAsset(selectedOption?.value.toString());
                            }}
                            options={assetOptions}
                            value={assetOptions.find(asset => asset.value === filterAsset)}
                        /> */}
                        <StyledSelect
                            className={classes.selectInput}
                            id={`${id}_operationSelect`}
                            menuPosition='fixed'
                            onChange={(selectedOption: SingleValue<SelectOption>) => {
                                setOperation(selectedOption?.value.toString());
                            }}
                            options={operations}
                            value={operations.find(opt => operation !== AgGridFilterType.ClearFilter && opt.value === operation)}
                        />
                        <Input
                            classes={{ input: classes.numberInput }}
                            id={`${id}_filterInput`}
                            inputProps={{ maxLength: 100 }}
                            onChange={(e) => {
                                const value = e.target.value;
                                setFilterValue(value);
                            }}
                            type='number'
                            value={filterValue}
                        />
                    </>
                );
            }
            default:
                return <div />;
        }
    };

    return renderInputType();
}

export default withStyles(muiStyles)(FilterInput);